import { useState } from 'react'
import { supabase } from '../../lib/supabase'
import { X, Buildings, Envelope, Phone, Image } from '@phosphor-icons/react'
import toast from 'react-hot-toast'

export default function CreateOrganizationModal({ isOpen, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    logoUrl: ''
  })
  const [logoFile, setLogoFile] = useState(null)
  const [logoPreview, setLogoPreview] = useState(null)

  function handleClose() {
    if (!loading) {
      setFormData({ name: '', email: '', phone: '', logoUrl: '' })
      setLogoFile(null)
      setLogoPreview(null)
      onClose()
    }
  }

  function handleLogoSelect(e) {
    const file = e.target.files[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file')
      return
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB')
      return
    }

    setLogoFile(file)
    
    const reader = new FileReader()
    reader.onload = (e) => {
      setLogoPreview(e.target.result)
    }
    reader.readAsDataURL(file)
  }

  async function uploadLogo() {
    if (!logoFile) return null

    setUploadingLogo(true)

    try {
      const fileExt = logoFile.name.split('.').pop()
      const fileName = `org-logo-${Date.now()}.${fileExt}`

      const { data, error } = await supabase.storage
        .from('wallet-assets')
        .upload(fileName, logoFile, {
          cacheControl: '3600',
          upsert: false
        })

      if (error) throw error

      const { data: { publicUrl } } = supabase.storage
        .from('wallet-assets')
        .getPublicUrl(data.path)

      return publicUrl
    } catch (error) {
      console.error('Logo upload error:', error)
      toast.error('Failed to upload logo')
      return null
    } finally {
      setUploadingLogo(false)
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()

    if (!formData.name.trim()) {
      toast.error('Organization name is required')
      return
    }

    if (!formData.email.trim()) {
      toast.error('Admin email is required')
      return
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Invalid email address')
      return
    }

    setLoading(true)

    try {
      let logoUrl = formData.logoUrl
      if (logoFile) {
        logoUrl = await uploadLogo()
        if (!logoUrl) {
          setLoading(false)
          return
        }
      }

      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session) {
        throw new Error('Not authenticated. Please login again.')
      }
      
      const response = await supabase.functions.invoke('send-org-admin-invitation', {
        body: {
          email: formData.email,
          orgName: formData.name,
          orgLogoUrl: logoUrl,
          orgPhone: formData.phone
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      })

      if (response.error) {
        if (response.error.context?.body) {
          try {
            const errorBody = JSON.parse(response.error.context.body)
            throw new Error(errorBody.error || 'Failed to send invitation')
          } catch (parseError) {
            // Failed to parse error body
          }
        }
        
        throw new Error(response.error.message || 'Failed to send invitation')
      }

      if (response.data?.error) {
        throw new Error(response.data.error)
      }

      toast.success('Organization invitation sent successfully!')
      handleClose()
      if (onSuccess) onSuccess()

    } catch (error) {
      console.error('Create organization error:', error)
      toast.error(error.message || 'Failed to send invitation')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-xl font-bold text-gray-900">Create Organization</h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X size={24} weight="light" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Organization Name *
            </label>
            <div className="relative">
              <Buildings
                size={18}
                weight="light"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Acme University"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Administrator Email *
            </label>
            <div className="relative">
              <Envelope
                size={18}
                weight="light"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="admin@example.com"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              They'll receive an invitation email to set up their admin account
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number (Optional)
            </label>
            <div className="relative">
              <Phone
                size={18}
                weight="light"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+1 (555) 123-4567"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Organization Logo (Optional)
            </label>
            
            {!logoPreview ? (
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-blue-400 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoSelect}
                  className="hidden"
                  id="logo-upload"
                />
                <label
                  htmlFor="logo-upload"
                  className="cursor-pointer flex flex-col items-center gap-2"
                >
                  <Image size={40} weight="light" className="text-gray-400" />
                  <p className="text-sm font-medium text-gray-700">
                    Click to upload logo
                  </p>
                  <p className="text-xs text-gray-500">
                    PNG, JPG up to 2MB
                  </p>
                </label>
              </div>
            ) : (
              <div className="relative">
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  className="w-full h-40 object-contain bg-gray-50 rounded-xl"
                />
                <button
                  type="button"
                  onClick={() => {
                    setLogoFile(null)
                    setLogoPreview(null)
                  }}
                  className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors"
                >
                  <X size={16} weight="bold" />
                </button>
              </div>
            )}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-sm text-blue-800">
              <strong>What happens next:</strong>
              <br />
              An invitation email will be sent to the administrator. They'll be able to complete their profile and start managing the organization.
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || uploadingLogo}
              className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading || uploadingLogo ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {uploadingLogo ? 'Uploading...' : 'Sending...'}
                </>
              ) : (
                'Send Invitation'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}